import cn from 'classnames';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { CandidatePipelineStatus } from 'client/types.gen';
import { IconCircleCheck } from 'components/UI/Icons/CircleCheck';
import { IconCircleDashed } from 'components/UI/Icons/CircleDashed';
import { IconEdit } from 'components/UI/Icons/Edit';
import { IconXCircle } from 'components/UI/Icons/XCircle';
import { Chip } from 'components/UI/Tailwind/Chip';
import { IconLightbulb1 } from './UI/Icons/Lightbulb1';
import { IconCircleCheckFill } from './UI/Icons/CircleCheckFill';

export type PipelineStatusChip = Chip & {
  status: CandidatePipelineStatus;
};

export function PipelineStatusIcon({
  status,
}: {
  status?: CandidatePipelineStatus;
}) {
  switch (status) {
    case 'suggested':
      return <IconLightbulb1 />;
    case 'ready_for_review':
      return <IconCircleDashed />;
    case 'approved':
      return <IconCircleCheck />;
    case 'denied':
      return <IconXCircle />;
    case 'saved':
      return <IconEdit />;
    case 'published':
    case 'scheduled':
      return <IconCircleCheckFill />;
    default:
      return null;
  }
}

export const PipelineStatusChip = forwardRef<
  HTMLButtonElement | HTMLSpanElement,
  PipelineStatusChip
>(({ className, disabled, status, ...rest }, ref) => {
  const { t } = useTranslation();

  return (
    <Chip
      ref={ref}
      className={cn(
        'font-semibold',
        {
          'bg-yellow-50 text-yellow-800': status === 'suggested',
          'bg-purple-50 text-purple-800': status === 'ready_for_review',
          'bg-teal-50 text-teal-800': status === 'approved',
          'bg-green-50 text-green-800':
            status === 'scheduled' || status === 'published',
          'bg-blue-50 text-blue-800': status === 'saved',
          'bg-red-50 text-red-800': status === 'denied',
          'opacity-40': disabled,
        },
        className,
      )}
      disabled={disabled}
      ellipsis
      iconStart={<PipelineStatusIcon status={status} />}
      size={22}
      variant="unstyled"
      {...rest}
    >
      {status === 'suggested' && t('dropdown.pipelineStatus.suggested')}
      {status === 'ready_for_review' &&
        t('dropdown.pipelineStatus.readyForReview')}
      {status === 'approved' && t('dropdown.pipelineStatus.approved')}
      {status === 'denied' && t('dropdown.pipelineStatus.denied')}
      {status === 'saved' && t('dropdown.pipelineStatus.saved')}
      {(status === 'published' || status === 'scheduled') &&
        t('dropdown.pipelineStatus.submitted')}
    </Chip>
  );
});
